import type { Notification as NotificationRecord } from '@ztechapps/common.contract'
import { getWindow, hasWindow } from '@ztechapps/common.utils'
import { useHistory } from '../useHistory'
import { useSubscription } from '../useSubscription'
import { useUser } from '../useUser'
import { notifcationQuery } from './graphql'
import { buildNotificationScopeFilter } from './notificationScopeFilter'

type CreatedNotificationData = { createdNotification?: NotificationRecord }

// Raises an operating-system notification for each notification created for the signed-in user while the
// tab is hidden or unfocused. It rides the created-notification subscription, which only delivers records
// created after it connects, filtered on the server to the user's own and tenant scopes.
export const useNativeNotificationListener = (): void => {
  const { user } = useUser()
  const { push } = useHistory()

  useSubscription<CreatedNotificationData>({
    query: notifcationQuery.subscription.created.query,
    filter: buildNotificationScopeFilter(user),
    onData: ({ data }) => {
      const notification = data?.data?.createdNotification
      if (!user?._id || !notification || !hasWindow()) return

      const win = getWindow()
      const { Notification } = win
      const isInBackground = win.document.hidden || !win.document.hasFocus()
      if (!Notification || Notification.permission !== 'granted' || !isInBackground) return

      // Nearly all mobile browsers throw from this constructor (they only allow a service worker to show one).
      try {
        const native = new Notification(notification.title, { body: notification.bodyPlain, tag: notification._id })
        native.onclick = () => {
          win.focus()
          if (notification.url) push(notification.url)
        }
      } catch {
        // Not supported here: nothing to raise.
      }
    },
  })
}
